import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { FadeUp, MaskedLines, ChapterNumber } from "@/components/Reveal";
import { TESTIMONIALS } from "@/data/site";
import { useLang, hlLines } from "@/i18n/LanguageContext";

const INTERVAL = 8000;
const STARS = ["a", "b", "c", "d", "e"];

const Stars = () => (
  <div className="flex gap-1" aria-label="5 stars">
    {STARS.map((k) => <Star key={k} className="h-4 w-4 fill-honey text-honey" />)}
  </div>
);

const Dots = ({ count, index, onPick }) => (
  <div className="flex gap-2" data-testid="testimonial-dots">
    {TESTIMONIALS.slice(0, count).map((t, i) => (
      <button
        key={t.id}
        onClick={() => onPick(i)}
        aria-label={`Show review ${i + 1}`}
        data-testid={`testimonial-dot-${i}`}
        className={`h-2 rounded-full transition-all duration-300 ${i === index ? "w-8 bg-brand" : "w-2 bg-ink/20 hover:bg-ink/40"}`}
      />
    ))}
  </div>
);

const QUOTE_MOTION = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -16 }, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } };

const QuoteCard = ({ item }) => {
  const { t } = useLang();
  return (
            <AnimatePresence mode="wait">
              <motion.blockquote
                key={item.id}
                {...QUOTE_MOTION}
                className="relative"
                data-testid={`testimonial-quote-${item.id}`}
              >
                <p className="font-display text-lg font-medium leading-relaxed text-ink sm:text-xl lg:text-2xl">"{item.quote}"</p>
                <footer className="mt-8 flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="font-display text-base font-semibold" data-testid="testimonial-name">{item.name}</p>
                    <p className="mt-1 font-mono text-[11px] uppercase tracking-widest text-mist">
                      {t("testimonials.parent")}{item.location ? ` · ${t(`locations.${item.location}.name`)}` : ""}
                    </p>
                  </div>
                  <Stars />
                </footer>
              </motion.blockquote>
            </AnimatePresence>
  );
};

const Controls = ({ count, index, onPick, onStep }) => (
            <div className="mt-10 flex items-center justify-between">
              <Dots count={count} index={index} onPick={onPick} />
              <div className="flex gap-2">
                <button onClick={() => onStep(-1)} aria-label="Previous review" data-testid="testimonial-prev"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-ink/15 transition-colors hover:border-brand hover:text-brand">
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button onClick={() => onStep(1)} aria-label="Next review" data-testid="testimonial-next"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-ink/15 transition-colors hover:border-brand hover:text-brand">
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
);

export const Testimonials = () => {
  const { t } = useLang();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = TESTIMONIALS.length;
  const step = useCallback((d) => setIndex((i) => (i + d + count) % count), [count]);

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => step(1), INTERVAL);
    return () => clearInterval(id);
  }, [paused, step]);

  const item = TESTIMONIALS[index];

  return (
    <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:py-32" data-testid="home-testimonials">
      <div className="grid gap-12 lg:grid-cols-12 lg:items-start">
        <FadeUp className="lg:col-span-4">
          <ChapterNumber n={t("testimonials.chapter")} />
          <h2 className="mt-4 font-display text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
            <MaskedLines lines={hlLines(t("testimonials.lines"))} />
          </h2>
          <p className="mt-4 max-w-sm text-base leading-relaxed text-mist">{t("testimonials.sub")}</p>
          <div className="mt-6 flex items-center gap-3">
            <Stars />
            <span className="font-mono text-xs uppercase tracking-widest text-mist">{t("testimonials.source")}</span>
          </div>
        </FadeUp>

        <FadeUp delay={0.15} className="lg:col-span-8">
          <div
            className="relative overflow-hidden rounded-[2rem] border border-ink/10 bg-white p-8 shadow-sm sm:p-12"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            data-testid="testimonial-card"
          >
            <Quote className="absolute -right-4 -top-4 h-32 w-32 text-sand" />
            <QuoteCard item={item} />
            <Controls count={count} index={index} onPick={setIndex} onStep={step} />
          </div>
        </FadeUp>
      </div>
    </section>
  );
};
